import { useState, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import { Table } from "components/ui/tables/Table/Table"
import { H3 } from "components/ui/typography/titles/Titles"
import { GripIcon } from "components/ui/icons/grip/GripIcon"
import { ModelNumberOrderHint } from "components/ui/modelNumberOrderHint/ModelNumberOrderHint"
import { useObjectCategoryDrawerStore } from "store/configuration.store"
import { ModelNumbersColumns } from "./ModelNumbersColumns"
import { useModelNumberReorder } from "./useModelNumberReorder.hook"
import { DetachModelNumberModal } from "./layout/DetachModelNumberModal/DetachModelNumberModal"
import { LinkModelNumberModal } from "./layout/LinkModelNumberModal/LinkModelNumberModal"
import style from "./ModelNumbers.module.scss"
import { PlusIcon } from "components/ui/icons/plus/PlusIcon"

const {
	customBorderedTable,
	modelNumnersStyle,
	topBlock,
	headingGroup,
	savingState,
	spinner,
	addModelNumberButton,
	hint,
	rowSlot,
	rowPicked,
	rowSaving,
	rowReverted,
	ghost,
	ghostRank,
	srOnly,
} = style

export type TModelNumberRow = {
	key: string
	position: number
	modelNumber: string
	width: string
	height: string
	depth: string
	isNew: boolean
}

const formatDimension = (value: unknown) => (Array.isArray(value) ? value.join(", ") : "")

export const ModelNumbers = () => {
	const { objectCategoryStore, modelNumberToId, newModelNumbers } = useObjectCategoryDrawerStore()
	const tableRef = useRef<HTMLDivElement>(null)
	const reorder = useModelNumberReorder(tableRef)

	const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)

	const [detachModal, setDetachModal] = useState<{
		isOpen: boolean
		modelNumberId?: number
		modelNumberName?: string
	}>({ isOpen: false })

	const handleDetach = (modelNumberId: number, modelNumberName: string) => {
		setDetachModal({ isOpen: true, modelNumberId, modelNumberName })
	}

	const handleDetachCancel = () => {
		setDetachModal({ isOpen: false })
	}

	const total = reorder.displayed.length

	const columns = ModelNumbersColumns({
		modelNumberToId,
		onDetach: handleDetach,
		total,
		isSaving: reorder.isSaving,
		onGripPointerDown: reorder.onGripPointerDown,
		onGripKeyDown: reorder.onGripKeyDown,
		onGripBlur: reorder.onGripBlur,
	})

	// Rows follow the saved order (`modelNumbers`), never the keys of `dimensions`, which JS re-sorts.
	const dataSource = useMemo<TModelNumberRow[]>(() => {
		const dimensions = objectCategoryStore.dimensions || {}
		return reorder.displayed.map((modelNumber, index) => {
			const d = dimensions[modelNumber] as unknown as Record<string, unknown> | undefined
			return {
				key: modelNumber,
				position: index + 1,
				modelNumber,
				width: formatDimension(d?.width),
				height: formatDimension(d?.height),
				depth: formatDimension(d?.depth),
				isNew: newModelNumbers.includes(modelNumber),
			}
		})
	}, [objectCategoryStore.dimensions, reorder.displayed, newModelNumbers])

	const rowClassName = (record: TModelNumberRow) =>
		[
			record.key === reorder.dragKey && rowSlot,
			record.key === reorder.pickedKey && rowPicked,
			record.key === reorder.savingKey && rowSaving,
			record.key === reorder.revertKey && rowReverted,
		]
			.filter(Boolean)
			.join(" ")

	const ghostRow = reorder.ghost && dataSource.find((row) => row.key === reorder.ghost?.key)

	return (
		<div className={modelNumnersStyle}>
			<div className={topBlock}>
				<div className={headingGroup}>
					<H3>Model numbers</H3>
					{reorder.isSaving && (
						<span className={savingState}>
							<span className={spinner} />
							Saving…
						</span>
					)}
				</div>
				{/* Linking while an order save is in flight would be undone by that save's revert. */}
				<button className={addModelNumberButton} onClick={() => setIsLinkModalOpen(true)} disabled={reorder.isSaving}>
					<PlusIcon />
					<span>Link model number</span>
				</button>
			</div>
			{total > 0 && (
				<ModelNumberOrderHint className={hint}>
					Processing checks these model numbers from top to bottom and picks the first one that matches the detected
					object.{total > 1 && " Drag rows to change the order."}
				</ModelNumberOrderHint>
			)}
			<span className={srOnly} aria-live="assertive">
				{reorder.announcement}
			</span>
			<div className={customBorderedTable} ref={tableRef}>
				<Table<TModelNumberRow>
					data={dataSource}
					columns={columns}
					pagination={false}
					rowClassName={rowClassName}
					bordered
				/>
			</div>
			{reorder.ghost &&
				ghostRow &&
				createPortal(
					<div
						className={ghost}
						style={{ left: reorder.ghost.x, top: reorder.ghost.y - reorder.ghost.offsetY, width: reorder.ghost.width }}
					>
						<GripIcon />
						<span className={ghostRank}>{ghostRow.position}</span>
						<span>{ghostRow.modelNumber}</span>
					</div>,
					document.body,
				)}
			<DetachModelNumberModal
				isModalOpen={detachModal.isOpen}
				modelNumberId={detachModal.modelNumberId}
				modelNumberName={detachModal.modelNumberName}
				onCancel={handleDetachCancel}
			/>
			<LinkModelNumberModal isModalOpen={isLinkModalOpen} onCancel={() => setIsLinkModalOpen(false)} />
		</div>
	)
}
